import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SiteCodesDisplay from './components/SiteCodesDisplay.component';

const EditFiberSiteModal = ({ open, onClose, selectedSites, onUpdate, isLoading }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  // Use external loading state if provided, otherwise use local state
  const [localLoading, setLocalLoading] = useState(false);
  const loading = isLoading !== undefined ? isLoading : localLoading;

  // Define all available fiber fields
  const allFields = [
    { key: 'site_code', label: 'Site Code', type: 'text', disabled: true },
    { key: 'project_name', label: 'Project Name', type: 'text' },
    { key: 'onfp_planning_remarks', label: 'ONFP Planning Remarks', type: 'text' },
    { key: 'bp_remarks', label: 'BP Remarks', type: 'text' },
    { key: 'comments', label: 'Comments', type: 'text' },
    { key: 'phase_name', label: 'Phase Name', type: 'text' },
    { key: 'bp', label: 'BP', type: 'text' },
    { key: 'fiber_site_name', label: 'Fiber Site Name', type: 'text' },
    { key: 'lat', label: 'Latitude', type: 'number' },
    { key: 'long', label: 'Longitude', type: 'number' },
    { key: 'technical_city', label: 'Technical City', type: 'text' },
    { key: 'region', label: 'Region', type: 'text' },
    { key: 'vendor', label: 'Vendor', type: 'text' },
    { key: 'fiber_pop_id', label: 'Fiber POP ID', type: 'text' },
    { key: 'ring_name', label: 'Ring Name', type: 'text' },
    { key: 'ring_unique_name', label: 'Ring Unique Name', type: 'text' },
    { key: 'ring_name_nms', label: 'Ring Name NMS', type: 'text' },
    { key: 'cutover_date', label: 'Cutover Date', type: 'date' },
    { key: 'total_length', label: 'Total Length', type: 'number' },
    { key: 'on_net', label: 'On Net', type: 'text' },
    { key: 'off_net', label: 'Off Net', type: 'text' },
    { key: 'prefix', label: 'Prefix', type: 'text' },
    { key: 'old_site_code', label: 'Old Site Code', type: 'text' },
    { key: 'replacement_site_code', label: 'Replacement Site Code', type: 'text' },
    { key: 'replacement_site_name', label: 'Replacement Site Name', type: 'text' },
    { key: 'replacement_site_lat', label: 'Replacement Site Latitude', type: 'number' },
    { key: 'replacement_site_long', label: 'Replacement Site Longitude', type: 'number' },
    {
      key: 'replacement_site_proposed_ring',
      label: 'Replacement Site Proposed Ring',
      type: 'text',
    },
    { key: 'addendum_status', label: 'Addendum Status', type: 'text' },
    { key: 'descope_status', label: 'Descope Status', type: 'text' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: ['On-Air', 'In-Progress', 'Planned', 'Non-Fiber', 'Descoped'],
    },
    {
      key: 'is_ftts',
      label: 'Is FTTS',
      type: 'select',
      options: ['Yes', 'No'],
    },
  ];

  const steps = ['Select Fields', 'Edit Values'];

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedFields([]);
      setFieldValues({});
    }
  }, [open]);

  const handleFieldSelection = (fieldKey, checked) => {
    if (checked) {
      setSelectedFields([...selectedFields, fieldKey]);
      // Initialize field value based on first selected site
      if (selectedSites && selectedSites.length > 0) {
        const firstSite = selectedSites[0];
        setFieldValues((prev) => ({
          ...prev,
          [fieldKey]: firstSite[fieldKey] || '',
        }));
      }
    } else {
      setSelectedFields(selectedFields.filter((field) => field !== fieldKey));
      const newFieldValues = { ...fieldValues };
      delete newFieldValues[fieldKey];
      setFieldValues(newFieldValues);
    }
  };

  const handleFieldValueChange = (fieldKey, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleNext = () => {
    if (selectedFields.length === 0) {
      alert('Please select at least one field to edit');
      return;
    }
    setActiveStep(1);
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleSubmit = async () => {
    if (selectedFields.length === 0) {
      alert('Please select at least one field to edit');
      return;
    }

    if (isLoading === undefined) {
      setLocalLoading(true);
    }
    try {
      // Prepare updated sites data
      const updatedSites = selectedSites.map((site) => {
        const updatedSite = { ...site };
        selectedFields.forEach((fieldKey) => {
          if (fieldValues[fieldKey] !== undefined) {
            updatedSite[fieldKey] = fieldValues[fieldKey];
          }
        });
        // Ensure off_net is always included if it exists in the site object
        if (site.hasOwnProperty('off_net')) {
          updatedSite.off_net =
            site.off_net !== undefined && site.off_net !== null ? site.off_net : '';
        }
        return updatedSite;
      });

      // Call the update function
      await onUpdate(updatedSites);

      // Close modal and reset
      onClose();
      setActiveStep(0);
      setSelectedFields([]);
      setFieldValues({});
    } catch (error) {
      console.error('Error updating sites:', error);
      alert('Error updating sites. Please try again.');
    } finally {
      if (isLoading === undefined) {
        setLocalLoading(false);
      }
    }
  };

  const renderFieldInput = (field) => {
    const value = fieldValues[field.key] || '';

    switch (field.type) {
      case 'select':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
              label={field.label}
            >
              {field.options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'date':
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            value={value}
            onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            value={value}
            onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            size="small"
            value={value}
            onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
            disabled={field.disabled}
          />
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minHeight: '600px',
        },
      }}
      // Add loading backdrop
      BackdropProps={{
        style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          // borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#344054' }}>
            Bulk Edit Fiber Sites
          </Typography>
          <SiteCodesDisplay selectedSites={selectedSites} maxVisible={4} showCount={true} />
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{
            color: '#667085',
            '&:hover': { backgroundColor: '#F5F5F5' },
            '&:disabled': { color: '#D0D5DD' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Loading Overlay for Modal Content */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: '8px',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#BD1D23', mb: 1 }}>
                Updating Sites...
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Please wait while we update the selected sites
              </Typography>
            </Box>
          </Box>
        )}

        {/* Horizontal Step Indicator */}
        <Box
          sx={{
            width: '100%',
            height: 52,
            position: 'relative',
            background: '#F5F5F5',
            borderRadius: 3,
            overflow: 'hidden',
            mb: 3,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '8%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              width: '84%',
            }}
          >
            {steps.map((text, index) => (
              <React.Fragment key={text}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      outline: `1px solid ${index === activeStep ? '#BD1D23' : '#ABB7C2'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: index === activeStep ? '#BD1D23' : '#ffffffff',
                      fontSize: 14,
                      fontWeight: 500,
                      fontFamily: 'Roboto',
                      backgroundColor: index < activeStep ? '#BD1D23' : 'transparent',
                    }}
                  >
                    {index < activeStep ? '✓' : index + 1}
                  </Box>
                  <Typography
                    sx={{
                      color:
                        index === activeStep
                          ? '#0D0B26'
                          : index < activeStep
                            ? '#BD1D23'
                            : '#ABB7C2',
                      fontSize: 14,
                      fontWeight: 500,
                      fontFamily: 'Roboto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      background: '#0D0B26',
                      mx: 2,
                      height: '0.6px',
                      opacity: '0.3',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* Step Content */}
        {activeStep === 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
              Select the fields you want to edit for all selected sites:
            </Typography>
            <Grid container spacing={2}>
              {allFields.map((field) => (
                <Grid item xs={12} sm={4} key={field.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFields.includes(field.key)}
                        disabled={field.key === 'site_code'}
                        onChange={(e) => handleFieldSelection(field.key, e.target.checked)}
                        sx={{
                          color: '#D0D5DD',
                          '&.Mui-checked': {
                            color: '#BD1D23',
                          },
                          '&.Mui-disabled': {
                            color: '#D0D5DD',
                          },
                        }}
                      />
                    }
                    label={field.label}
                    sx={{
                      opacity: field.key === 'site_code' ? 0.6 : 1,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px',
                        color: field.key === 'site_code' ? '#9E9E9E' : '#344054',
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
              Enter new values for the selected fields. These values will be applied to all selected
              sites:
            </Typography>
            <Grid container spacing={2}>
              {selectedFields.map((fieldKey) => {
                const field = allFields.find((f) => f.key === fieldKey);
                return (
                  <Grid item xs={12} sm={6} key={fieldKey}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#344054', mb: 1, fontWeight: 500 }}>
                        {field.label}
                      </Typography>
                      {renderFieldInput(field)}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #E0E0E0' }}>
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
          {activeStep === 1 && (
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              variant="outlined"
              sx={{
                color: '#667085',
                borderColor: '#D0D5DD',
                '&:hover': {
                  borderColor: '#98A2B3',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Back
            </Button>
          )}

          <Button
            onClick={activeStep === 0 ? handleNext : handleSubmit}
            disabled={activeStep === 0 ? selectedFields.length === 0 : loading}
            variant="contained"
            sx={{
              backgroundColor: '#BD1D23',
              '&:hover': {
                backgroundColor: '#A0151A',
              },
              '&:disabled': {
                backgroundColor: '#D0D5DD',
                color: '#98A2B3',
              },
            }}
          >
            {activeStep === 0 ? 'Continue' : loading ? 'Updating...' : 'Update Sites'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditFiberSiteModal;
